import Logo from "./Logo";
import Navigation from "./Navigation";
import Language from "./Language";
import ContactButton from "./ContactButton";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import { useLanguage } from "@/contexts/I18nProvider";
import { useKrpano } from "@/contexts/KrpanoProvider";

export default function Header({ 
    isShowSectors, 
    onToggleSectors,
    hideFloatingButtons
}: { 
    isShowSectors?: boolean; 
    onToggleSectors?: () => void;
    hideFloatingButtons?: boolean;
}) {
    const pathname = usePathname();
    const isMap3dPage = pathname?.includes('/map3d') ?? false;
    const { locale } = useLanguage();
    const { isTurnOnSound, setIsTurnOnSound } = useKrpano();
    const [showVoiceText, setShowVoiceText] = useState(true);

    useEffect(() => {
        if (!isTurnOnSound) {
            setShowVoiceText(true);
            const timer = setTimeout(() => {
                setShowVoiceText(false);
            }, 5000);
            return () => clearTimeout(timer);
        } else {
            setShowVoiceText(false);
        }
    }, [isTurnOnSound]);

    const toggleMute = () => {
        setIsTurnOnSound(prev => !prev);
    };

    const [provinceMode, setProvinceMode] = useState<'34' | '63'>('34');

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const savedMode = localStorage.getItem('provinceMode') as '34' | '63';
            if (savedMode === '34' || savedMode === '63') {
                setProvinceMode(savedMode);
            }
        }

        const handleSwitchProvinceMode = (e: any) => {
            const mode = e.detail?.mode;
            if (mode === '34' || mode === '63') {
                setProvinceMode(mode);
            }
        };

        window.addEventListener('switch_province_mode', handleSwitchProvinceMode);
        return () => {
            window.removeEventListener('switch_province_mode', handleSwitchProvinceMode);
        };
    }, []);

    const toggleProvinceMode = (mode: '34' | '63') => {
        setProvinceMode(mode);
        if (typeof window !== 'undefined') {
            localStorage.setItem('provinceMode', mode);
            window.dispatchEvent(new CustomEvent('switch_province_mode', { detail: { mode } }));
        }
    };

    return (
        <>
        <header className="px-4 h-16 text-white bg-[rgba(5,21,6,0.8)] backdrop-blur-[32px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] flex gap-4 justify-between items-center fixed top-0 left-0 right-0 z-[140] text-sm">
            <div className="flex items-center gap-3 lg:gap-4">
                <Logo />
                {/* <Navigation /> */}
            </div>
            <div className="flex items-center gap-4">
                {/* Province mode toggle - only shown on digital map page */}
                {isMap3dPage && (
                    <div className="flex items-stretch rounded-full overflow-hidden border border-white/20 h-9 p-0.5 bg-black/40 backdrop-blur-md">
                        <button
                            onClick={() => toggleProvinceMode('34')}
                            className={`text-[11px] font-bold whitespace-nowrap px-3.5 rounded-full transition-all duration-200 cursor-pointer ${
                                provinceMode === '34'
                                    ? 'bg-[#FFF500] text-black shadow-sm'
                                    : 'text-white/80 hover:text-white hover:bg-white/10'
                            }`}
                        >
                            {locale === 'en' ? '34 Provinces & Cities' : locale === 'ja' ? '34省・市' : '34 Tỉnh Thành'}
                        </button>
                        <button
                            onClick={() => toggleProvinceMode('63')}
                            className={`text-[11px] font-bold whitespace-nowrap px-3.5 rounded-full transition-all duration-200 cursor-pointer ${
                                provinceMode === '63'
                                    ? 'bg-[#FFF500] text-black shadow-sm'
                                    : 'text-white/80 hover:text-white hover:bg-white/10'
                            }`}
                        >
                            {locale === 'en' ? '63 Provinces & Cities' : locale === 'ja' ? '63省・市' : '63 Tỉnh Thành'}
                        </button>
                    </div>
                )}
                <ContactButton />
                <Language />
            </div>
        </header>
        <div className={`fixed top-[84px] py-1 left-4 z-50 flex flex-col gap-3 items-start transition-opacity duration-300 ${isShowSectors || hideFloatingButtons ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            <a
                href="https://starglobal3d.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#00000080] backdrop-blur-[28.24px] text-white rounded-full p-4 cursor-pointer transition-colors hover:bg-[#00000099] shadow-lg flex items-center justify-center aspect-square"
            >
                <svg width="34" height="35" viewBox="0 0 34 35" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-5">
                    <path d="M7.28906 23.2814L1.50006 17.4924L7.28906 11.7034" stroke="currentColor" strokeWidth="4" strokeLinejoin="round"/>
                    <path d="M15.6094 17.493H1.5011" stroke="currentColor" strokeWidth="4" strokeLinejoin="round"/>
                    <path d="M14.5859 10.0556V6.03703C14.5859 3.5313 16.6172 1.5 19.123 1.5H27.8747C30.3804 1.5 32.4117 3.5313 32.4117 6.03703V28.5819C32.4117 31.0877 30.3804 33.1189 27.8747 33.1189H19.123C16.6172 33.1189 14.5859 31.0876 14.5859 28.5819V24.9296" stroke="currentColor" strokeWidth="4"/>
                </svg>
                {/* {locale === 'en' ? 'Home' : 'Trang chủ'} */}
            </a>
            
            <button className={`group ${isTurnOnSound ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-[#00000080] text-white hover:bg-[#00000099]'} backdrop-blur-[28.24px] rounded-full p-4 cursor-pointer transition-all duration-500 shadow-lg flex items-center justify-center overflow-hidden whitespace-nowrap`} onClick={toggleMute}>
                <svg className="size-5 shrink-0" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip_sound_icon)">
                        {isTurnOnSound ? (
                            <>
                                <path d="M9.70703 21.4994C8.60246 21.4994 7.70703 22.3948 7.70703 23.4994V40.5004C7.70703 41.605 8.60246 42.5004 9.70703 42.5004H21.7077L39.2086 60.0013V3.99854L21.7077 21.4994H9.70703Z" fill="currentColor"/>
                                <path d="M46.21 17.8945C51.3902 20.4847 54.9604 25.8049 54.9604 32.0002C54.9604 38.1955 51.3902 43.5158 46.21 46.0709V17.8945Z" fill="currentColor"/>
                            </>
                        ) : (
                            <>
                                <path d="M43.2373 23.9492L59.3399 40.0504" stroke="currentColor" strokeWidth="4"/>
                                <path d="M59.3384 23.9492L43.2373 40.0504" stroke="currentColor" strokeWidth="4"/>
                                <path d="M9.11523 21.4994C8.01066 21.4994 7.11523 22.3948 7.11523 23.4994V40.5004C7.11523 41.605 8.01066 42.5004 9.11523 42.5004H21.1159L38.6168 60.0013V3.99854L21.1159 21.4994H9.11523Z" fill="currentColor"/>
                            </>
                        )}
                    </g>
                    <defs>
                        <clipPath id="clip_sound_icon">
                            <rect width="64" height="64" fill="white"/>
                        </clipPath>
                    </defs>
                </svg>
                <div className={`transition-all duration-500 overflow-hidden text-sm font-medium flex items-center ${showVoiceText ? 'max-w-[200px] opacity-100 ml-3' : 'max-w-0 opacity-0 ml-0 group-hover:max-w-[200px] group-hover:opacity-100 group-hover:ml-3'}`}>
                    {isTurnOnSound 
                        ? (locale === 'en' ? 'Turn off introduction' : locale === 'ja' ? '紹介ナレーションをオフにする' : 'Tắt thuyết minh giới thiệu') 
                        : (locale === 'en' ? 'Turn on introduction' : locale === 'ja' ? '紹介ナレーションをオンにする' : 'Bật thuyết minh giới thiệu')
                    }
                </div>
            </button>
        </div>
        </>
    );
}
